// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { useEffect } from 'react';

/**
 * Keeps a `--keyboard-inset` CSS variable on <html> in sync with the height of
 * the on-screen keyboard, using the VisualViewport API.
 *
 * Why this is needed: when the soft keyboard opens on mobile, iOS Safari shrinks
 * only the *visual* viewport — the layout viewport (and `100dvh`) stay full size,
 * so a `position: absolute; bottom: 0` input ends up hidden behind the keyboard.
 * This hook measures the gap (layout height − visual height − scroll offset) and
 * exposes it so the input can be lifted by exactly that amount.
 *
 * On browsers where the keyboard resizes the layout viewport instead (Android
 * Chrome with `interactive-widget=resizes-content`), the gap stays ~0 and the
 * lift is a no-op — the layout already did the work.
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    if (!vv) {
      root.style.setProperty('--keyboard-inset', '0px');
      return;
    }

    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--keyboard-inset', `${Math.round(inset)}px`);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      root.style.removeProperty('--keyboard-inset');
    };
  }, []);
}
